import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { MapPin, Navigation, AlertTriangle, ShieldCheck, Calendar, ArrowRight, Pencil, XCircle, Play, Square, Info, Receipt as ReceiptIcon, Video, Banknote, Camera } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Trip } from '../../types/trip';
import type { UserRole } from '../../types/user';
import { useTripDetail } from '../../hooks/useTripDetail';
import { useAuth } from '../../context/AuthContext';
import { hayFinanzas } from '../../utils/features';
import { useVehicleCameras } from '../../hooks/useVehicleCameras';
import { HistoricalVideoPlayer } from '../vehicles/HistoricalVideoPlayer';
import { AlertExpandedModal } from '../alerts/AlertExpandedModal';
import { closeAlert, updateExpense, updateLogbookEntry, getTripExpenses, getTripLogbookEntries } from '../../lib/api/endpoints';
import type { ExpensePatchDto, LogbookEntryPatchDto } from '../../lib/api/endpoints';
import type { AlertEvent } from '../../types/alert';
import { tripMetricsFrom } from '../../lib/api/mappers';
import { TripStatusBadge } from './TripStatusBadge';
import { TripTimeline } from './TripTimeline';
import { LogbookTimeline, LogbookTimelineHeader } from '../logbook/LogbookTimeline';
import { LogbookEntryDetailModal } from '../logbook/LogbookEntryDetailModal';
import { LogbookEntryFormModal } from '../logbook/LogbookEntryFormModal';
import { puedeCorregir as puedeCorregirNovedad, dentroDeVentanaEdicion as ventanaNovedad, puedeAdjuntar } from '../../utils/logbook';
import type { LogbookEntry } from '../../types/logbook';
import { ExpensesTable } from '../expenses/ExpensesTable';
import { ExpenseDetailModal } from '../expenses/ExpenseDetailModal';
import { ExpenseFormModal } from '../expenses/ExpenseFormModal';
import { puedeSubirRecibo, sumarMontos, formatearCOP, puedeCorregir as puedeCorregirGasto, dentroDeVentanaEdicion as ventanaGasto } from '../../utils/expenses';
import type { Expense } from '../../types/expense';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { RevenuesTable } from '../finance/RevenuesTable';
import { RevenueDetailModal } from '../finance/RevenueDetailModal';
import { puedeCorregirIngreso, puedeRegistrarIngreso, puedeVerIngresos, dentroDeVentanaEdicionIngreso } from '../../utils/finance';
import type { Revenue } from '../../types/finance';
import { useLogbookTypes } from '../../hooks/useLogbookTypes';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import {
  formatDateTime,
  formatDesviacion,
  formatDuration,
  desviacionFinMin,
  desviacionInicioMin,
  puedeCancelar,
  puedeCorregir,
  puedeFinalizar,
  puedeIniciar,
  puedeAdjuntarManifiesto,
  cobraConTarifa,
  faltaManifiesto,
} from '../../utils/trips';
import { detectStops, routeDistanceKm, formatApproxDistanceKm } from '../../utils/vehicleHistory';
import { routeStopIcon } from '../map/routeIcons';
import { SEVERITY_COLORS } from '../../utils/constants';

interface TripDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  trip: Trip | null;
  role?: UserRole;
  miDriverId?: string;
  onCorregir?: (trip: Trip) => void;
  onCancelar?: (trip: Trip) => void;
  onIniciar?: (trip: Trip) => void;
  onFinalizar?: (trip: Trip) => void;
  /** FE-34 — abre «Agregar manifiesto» para un viaje en curso sin la foto. */
  onAdjuntarManifiesto?: (trip: Trip) => void;
}

const startIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;gap:4px;padding:4px 8px;border-radius:99px;background:#10B981;color:#FFFFFF;border:2px solid #FFFFFF;box-shadow:0 4px 12px rgba(16,185,129,0.6);font-size:11px;font-weight:800;white-space:nowrap;"><span>🏁</span> INICIO</div>`,
  iconSize: [80, 26],
  iconAnchor: [40, 13],
});

const endIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;gap:4px;padding:4px 8px;border-radius:99px;background:#EF4444;color:#FFFFFF;border:2px solid #FFFFFF;box-shadow:0 4px 12px rgba(239,68,68,0.6);font-size:11px;font-weight:800;white-space:nowrap;"><span>📍</span> FINAL</div>`,
  iconSize: [85, 26],
  iconAnchor: [42, 13],
});

const alertPointIcon = L.divIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#F59E0B;border:2.5px solid #FFFFFF;box-shadow:0 0 10px rgba(245,158,11,0.8);display:flex;align-items:center;justify-content:center;color:#FFFFFF;font-size:13px;">⚠️</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function RouteFitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
      map.invalidateSize();
    }
  }, [map, points]);
  return null;
}

function Metrica({ label, valor, nota }: { label: string; valor: string; nota?: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
      <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>{label}</span>
      <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)' }}>{valor}</span>
      {nota && <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>{nota}</span>}
    </div>
  );
}

/**
 * FE-11 — el expediente completo de un viaje, todo derivado de su ventana.
 *
 * Nada de lo que se ve aquí está estampado en el viaje: la ruta son las
 * posiciones del vehículo dentro de `validFrom`–`validTo` y las alertas son
 * las suyas en ese mismo rango (ADR-024). Por eso corregir el viaje cambia
 * esta pantalla entera sin que nadie toque la telemetría, y por eso el hook
 * vuelve a pedir los tres bloques cuando la ventana cambia.
 *
 * FE-13 le añadió las NOVEDADES, que son el mismo concepto llevado a lo que
 * cuenta una persona: la varada que alguien relató entra en este expediente
 * porque su `occurredAt` cae dentro de la ventana, sin que nadie la asigne.
 *
 * Lo que NO se ve, a propósito: la distancia recorrida. El kilometraje llega
 * del equipo y el core lo descarta; sumar los tramos de la polyline daría un
 * número creíble que no es el odómetro. Mostrarlo sería inventar un dato, que
 * es justo lo que esta pantalla dejó de hacer al salir de los mocks.
 */
export function TripDetailModal({
  isOpen, onClose, trip, role, miDriverId,
  onCorregir, onCancelar, onIniciar, onFinalizar, onAdjuntarManifiesto,
}: TripDetailModalProps) {
  const { route, alerts, transitions, logbook, expenses, revenues, isLoading, error, reload } = useTripDetail(isOpen ? trip : null);
  // FE-35 (ADR-034): sin el módulo, el bloque «Tarifa» y los ingresos del viaje no existen.
  const { user } = useAuth();
  const conFinanzas = hayFinanzas(user);
  const [ingresoAbierto, setIngresoAbierto] = useState<Revenue | null>(null);
  const [novedadAbierta, setNovedadAbierta] = useState<LogbookEntry | null>(null);
  const [gastoAbierto, setGastoAbierto] = useState<Expense | null>(null);

  // Paridad de edición con los paneles de Gastos y Novedades: desde el
  // expediente también se corrige (no solo se mira). Los catálogos y las
  // asociaciones son los mismos que usan esos paneles, así que el formulario de
  // corrección se comporta igual aquí.
  const { categories } = useExpenseCategories();
  const { activos: tiposNovedad } = useLogbookTypes();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();
  const [corrigiendoGasto, setCorrigiendoGasto] = useState<Expense | null>(null);
  const [corrigiendoNovedad, setCorrigiendoNovedad] = useState<LogbookEntry | null>(null);
  // ADR-024: corregir la fecha puede sacar el ítem de la ventana de ESTE viaje.
  // Cuando pasa, lo decimos explícito en vez de que simplemente desaparezca.
  const [avisoFuera, setAvisoFuera] = useState<string | null>(null);

  const corregirGasto = async (id: string, data: ExpensePatchDto) => {
    await updateExpense(id, data);
    setCorrigiendoGasto(null);
    reload();
    if (trip) {
      try {
        const page = await getTripExpenses(trip.id);
        if (!page.items.some(e => e.id === id)) {
          setAvisoFuera('El gasto salió de la ventana de este viaje por el cambio de fecha. Lo encuentras en el panel de Gastos.');
        }
      } catch { /* el aviso es informativo: si la comprobación falla, no molestamos */ }
    }
  };

  const corregirNovedad = async (id: string, data: LogbookEntryPatchDto) => {
    await updateLogbookEntry(id, data);
    setCorrigiendoNovedad(null);
    reload();
    if (trip) {
      try {
        const page = await getTripLogbookEntries(trip.id);
        if (!page.items.some(e => e.id === id)) {
          setAvisoFuera('La novedad salió de la ventana de este viaje por el cambio de fecha. La encuentras en el panel de Novedades.');
        }
      } catch { /* informativo */ }
    }
  };
  // #193 — video histórico del tramo, embebido y pre-cargado con una ventana.
  // Se monta solo al pulsar "Ver video", para no abrir HLS en cada expediente.
  // Las cámaras se piden cuando el modal está abierto.
  // #196 — `videoWindow` es la ventana que carga el reproductor: la del viaje
  // entero ("Ver video del tramo") o la de una parada concreta ("Ver video de
  // esta parada"). El reproductor solo lee su ventana al montar, así que
  // cambiarla lo remonta vía `key`.
  const [videoWindow, setVideoWindow] = useState<{ from: string; to: string } | null>(null);
  const [alertaAbierta, setAlertaAbierta] = useState<AlertEvent | null>(null);
  const { cameras } = useVehicleCameras(isOpen ? trip?.vehicleId ?? null : null);
  useEffect(() => {
    setVideoWindow(null);
    setAlertaAbierta(null);
    setCorrigiendoGasto(null);
    setCorrigiendoNovedad(null);
    setAvisoFuera(null);
  }, [trip?.id, isOpen]);
  const puedeVerVideo = role !== 'operator' && cameras.length > 0;

  const puntos = useMemo<[number, number][]>(
    () => (route?.points ?? []).map(p => [p.lat, p.lng] as [number, number]),
    [route],
  );

  const metricas = useMemo(() => (trip ? tripMetricsFrom(trip, route) : null), [trip, route]);

  // #192 — paradas del recorrido en el expediente (misma lógica que el
  // histórico de recorrido: `detectStops` sobre los puntos de la ventana).
  const paradas = useMemo(() => (route ? detectStops(route.points) : []), [route]);
  // #194 — distancia aprox. por GPS, la misma que calcula el histórico de
  // recorrido (longitud de la polyline, NO el odómetro del equipo).
  const distanciaKm = useMemo(() => (route ? routeDistanceKm(route.points) : null), [route]);

  if (!trip) return null;

  const desvioInicio = formatDesviacion(desviacionInicioMin(trip));
  const desvioFin = formatDesviacion(desviacionFinMin(trip));
  const hayRuta = puntos.length > 0;

  const acciones = [
    onIniciar && puedeIniciar(role, trip, miDriverId) && (
      <Button key="iniciar" size="sm" leftIcon={<Play size={14} />} onClick={() => onIniciar(trip)}>Iniciar</Button>
    ),
    onFinalizar && puedeFinalizar(role, trip, miDriverId) && (
      <Button key="finalizar" size="sm" variant="success" leftIcon={<Square size={14} />} onClick={() => onFinalizar(trip)}>Finalizar</Button>
    ),
    // FE-34 — la foto del manifiesto se puede poner en cualquier momento del
    // viaje en curso; la UI la exige para finalizar (ADR-032 §4.2).
    onAdjuntarManifiesto && puedeAdjuntarManifiesto(role, trip, miDriverId) && (
      <Button key="manifiesto" size="sm" variant="secondary" leftIcon={<Camera size={14} />} onClick={() => onAdjuntarManifiesto(trip)}>Agregar manifiesto</Button>
    ),
    onCorregir && puedeCorregir(role, trip) && (
      <Button key="corregir" size="sm" variant="secondary" leftIcon={<Pencil size={14} />} onClick={() => onCorregir(trip)}>Corregir</Button>
    ),
    onCancelar && puedeCancelar(role, trip) && (
      <Button key="cancelar" size="sm" variant="danger" leftIcon={<XCircle size={14} />} onClick={() => onCancelar(trip)}>Cancelar viaje</Button>
    ),
  ].filter(Boolean);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Recorrido · ${trip.vehiclePlate || 'Sin placa'}`}
      size="lg"
      footer={acciones.length > 0 ? <>{acciones}</> : undefined}
    >
      <div className="trip-detail-modal" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', background: 'var(--bg-raised)', padding: '0.875rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Calendar size={18} style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {trip.window
                ? `${formatDateTime(trip.window.from)} → ${formatDateTime(trip.window.to)}`
                : `Programado ${formatDateTime(trip.scheduledFrom)}`}
            </span>
            {trip.driverName && (
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>· {trip.driverName}</span>
            )}
          </div>
          <TripStatusBadge status={trip.status} size="md" />
        </div>

        {avisoFuera && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.75rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12.5px' }}>
            <Info size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ flex: 1 }}>{avisoFuera}</span>
            <button onClick={() => setAvisoFuera(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 800, fontSize: '13px', lineHeight: 1 }} aria-label="Cerrar aviso">×</button>
          </div>
        )}

        {trip.status === 'cancelled' && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.875rem 1rem', background: 'rgba(107,114,128,0.12)', border: '1px solid rgba(107,114,128,0.3)', borderRadius: 'var(--radius-lg)', color: 'var(--text-secondary)', fontSize: '13px' }}>
            <XCircle size={18} style={{ flexShrink: 0 }} />
            <span>
              Viaje cancelado{trip.cancelledReason ? <>: <em>“{trip.cancelledReason}”</em></> : '.'} Su ventana
              quedó vacía, así que no tiene recorrido ni eventos asociados — no es que no se hayan
              cargado.
            </span>
          </div>
        )}

        {error && (
          <div style={{ padding: '0.875rem 1rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '13px' }}>
            No se pudo cargar el expediente del viaje: {error.message}
          </div>
        )}

        {hayRuta ? (
          <div style={{ position: 'relative', width: '100%', height: '300px', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border)' }}>
            <MapContainer center={puntos[0]} zoom={11} style={{ width: '100%', height: '100%', minHeight: '300px' }} zoomControl={false}>
              <TileLayer
                attribution="&copy; Esri World Imagery Satellite"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                opacity={0.8}
              />
              <RouteFitBounds points={puntos} />
              <Polyline positions={puntos} color="#000000" weight={8} opacity={0.4} />
              <Polyline positions={puntos} color="#3B82F6" weight={5} opacity={0.9} />

              <Marker position={puntos[0]} icon={startIcon}>
                <Popup>
                  <div style={{ padding: '4px' }}>
                    <p style={{ margin: 0, fontWeight: 800, color: '#10B981', fontSize: '12px' }}>🏁 PRIMER PUNTO MEDIDO</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: 600 }}>{trip.origin}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>{formatDateTime(route!.points[0].occurredAt)}</p>
                  </div>
                </Popup>
              </Marker>

              <Marker position={puntos[puntos.length - 1]} icon={endIcon}>
                <Popup>
                  <div style={{ padding: '4px' }}>
                    <p style={{ margin: 0, fontWeight: 800, color: '#EF4444', fontSize: '12px' }}>📍 ÚLTIMO PUNTO MEDIDO</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: 600 }}>{trip.destination}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>{formatDateTime(route!.points[route!.points.length - 1].occurredAt)}</p>
                  </div>
                </Popup>
              </Marker>

              {alerts.filter(a => a.lat !== 0 || a.lng !== 0).map(a => (
                <Marker key={a.id} position={[a.lat, a.lng]} icon={alertPointIcon}>
                  <Popup>
                    <div style={{ padding: '4px' }}>
                      <p style={{ margin: 0, fontWeight: 800, color: SEVERITY_COLORS[a.severity], fontSize: '12px' }}>⚠️ EVENTO DURANTE EL VIAJE</p>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: 600 }}>{a.subCategory ?? a.category}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>{formatDateTime(a.timestamp)}</p>
                      <button
                        onClick={() => setAlertaAbierta(a)}
                        style={{ marginTop: '6px', border: 'none', background: SEVERITY_COLORS[a.severity], color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        Ver alerta →
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {paradas.map(p => (
                <Marker key={`parada-${p.arrivedAt}`} position={[p.lat, p.lng]} icon={routeStopIcon}>
                  <Popup>
                    <div style={{ padding: '4px' }}>
                      <p style={{ margin: 0, fontWeight: 800, color: '#F59E0B', fontSize: '12px' }}>⏸️ PARADA · {formatDuration(Math.round(p.durationMin))}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>Llegada {formatDateTime(p.arrivedAt)}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>Salida {formatDateTime(p.leftAt)}</p>
                      {puedeVerVideo && (
                        <button
                          onClick={() => setVideoWindow({ from: p.arrivedAt, to: p.leftAt })}
                          style={{ marginTop: '6px', border: 'none', background: '#F59E0B', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '6px', cursor: 'pointer' }}
                        >
                          Ver video de esta parada →
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : (
          <div style={{ padding: '1.5rem', textAlign: 'center', background: 'var(--bg-raised)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg)', color: 'var(--text-muted)', fontSize: '13px' }}>
            {isLoading
              ? 'Cargando el recorrido…'
              : trip.status === 'planned'
                ? 'El viaje aún no ha empezado: no hay recorrido que mostrar.'
                : trip.window
                  ? 'El equipo no reportó posiciones dentro de la ventana de este viaje.'
                  : 'Este viaje no tiene ventana de tiempo, así que no hay recorrido asociado.'}
          </div>
        )}

        {route?.downsampled && (
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '11px', color: 'var(--text-muted)' }}>
            <Info size={13} /> El servidor adelgazó la polyline: se conserva la forma del recorrido, no cada punto medido.
          </p>
        )}

        {/* #193/#196 — video histórico embebido y pre-cargado con una ventana:
            la del viaje entero, o la de una parada concreta (botón en su popup
            del mapa). Solo para quien puede ver histórico (el operador recibe
            403) y si el vehículo tiene cámaras. */}
        {trip.window && puedeVerVideo && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {!videoWindow ? (
              <Button variant="secondary" size="sm" leftIcon={<Video size={14} />} onClick={() => setVideoWindow({ from: trip.window!.from, to: trip.window!.to })} style={{ alignSelf: 'flex-start' }}>
                Ver video del tramo
              </Button>
            ) : (
              <>
                <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  <Video size={14} /> {formatDateTime(videoWindow.from)} → {formatDateTime(videoWindow.to)}
                </p>
                <HistoricalVideoPlayer
                  key={`${videoWindow.from}-${videoWindow.to}`}
                  vehicleId={trip.vehicleId}
                  cameras={cameras}
                  initialFrom={videoWindow.from}
                  initialTo={videoWindow.to}
                  onClose={() => setVideoWindow(null)}
                />
              </>
            )}
          </div>
        )}

        <div style={{ background: 'var(--bg-raised)', padding: '1rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(59,130,246,0.12)', color: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Navigation size={16} />
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Origen</span>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{trip.origin}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>
                Programado {formatDateTime(trip.scheduledFrom)}
                {trip.actualFrom && <> · real {formatDateTime(trip.actualFrom)}{desvioInicio ? ` (${desvioInicio})` : ''}</>}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '1rem', color: 'var(--text-muted)' }}>
            <ArrowRight size={16} style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: '12px', fontWeight: 600 }}>
              {metricas?.durationMinutes !== undefined ? formatDuration(metricas.durationMinutes) : 'Sin ventana medible'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(16,185,129,0.12)', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MapPin size={16} />
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Destino</span>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{trip.destination}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>
                Programado {formatDateTime(trip.scheduledTo)}
                {trip.actualTo && <> · real {formatDateTime(trip.actualTo)}{desvioFin ? ` (${desvioFin})` : ''}</>}
              </span>
            </div>
          </div>

          {/* FE-34 — con qué se cobra este viaje (ADR-032). Nombres, nunca
              dinero: el Operador también ve este bloque (#225). */}
          {conFinanzas && cobraConTarifa(trip) && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(139,92,246,0.12)', color: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ReceiptIcon size={16} />
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Tarifa</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {trip.customerName ?? 'Cliente'}{trip.tariffName ? <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> · {trip.tariffName}</span> : null}
                </span>
                <span style={{ fontSize: '11px', display: 'block', color: faltaManifiesto(trip) ? '#F59E0B' : trip.hasManifest ? '#10B981' : 'var(--text-muted)', fontWeight: 600 }}>
                  {trip.hasManifest
                    ? 'Manifiesto adjunto'
                    : trip.revenueId
                      ? (trip.status === 'in_progress' ? 'Sin manifiesto: hace falta para finalizar' : 'Sin manifiesto')
                      : 'El ingreso se crea al iniciar, con el peso del manifiesto'}
                </span>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <Metrica label="Duración" valor={formatDuration(metricas?.durationMinutes)} nota="de la ventana del viaje" />
          <Metrica
            label="Velocidad promedio"
            valor={metricas?.avgSpeedKmh !== undefined ? `${metricas.avgSpeedKmh} km/h` : '—'}
            nota={metricas?.pointCount ? `${metricas.pointCount} puntos medidos` : 'sin telemetría'}
          />
          <Metrica
            label="Velocidad máxima"
            valor={metricas?.maxSpeedKmh !== undefined ? `${metricas.maxSpeedKmh} km/h` : '—'}
          />
          <Metrica
            label="Distancia"
            valor={distanciaKm !== null ? formatApproxDistanceKm(distanciaKm) : 'No disponible'}
            nota={distanciaKm !== null ? (route?.downsampled ? 'aprox. por GPS (polyline adelgazada, sale corta)' : 'aprox. por GPS, no el odómetro') : 'sin recorrido'}
          />
          <Metrica label="Paradas" valor={hayRuta ? String(paradas.length) : '—'} nota={hayRuta ? 'marcadas en el mapa' : 'sin recorrido'} />
        </div>

        {alerts.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B' }}>
              <AlertTriangle size={18} />
              <span style={{ fontSize: '13px', fontWeight: 600 }}>
                {alerts.length} evento(s) ocurrieron dentro de la ventana de este viaje
              </span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: '180px', overflowY: 'auto' }}>
              {alerts.map(a => (
                // #195 — abre el detalle de la alerta (info + evidencia en video).
                <li
                  key={a.id}
                  onClick={() => setAlertaAbierta(a)}
                  title="Ver detalle de la alerta"
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '12px', padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderLeft: `3px solid ${SEVERITY_COLORS[a.severity]}`, borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
                >
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{a.subCategory ?? a.category}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--text-muted)' }}>
                    {formatDateTime(a.timestamp)}
                    <ArrowRight size={13} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : !isLoading && trip.window && trip.status !== 'planned' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 'var(--radius-lg)', color: '#10B981' }}>
            <ShieldCheck size={18} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Trayecto limpio: sin eventos dentro de la ventana.</span>
          </div>
        ) : null}

        {/* Las novedades se leen de la más ANTIGUA a la más reciente: aquí el
            viaje se lee como una historia, de la salida a la llegada. En la
            página de Novedades el orden es el contrario, y por eso lo decide
            quien usa el componente. El backend ya las entrega así. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <LogbookTimelineHeader total={logbook.length} />
          <LogbookTimeline
            entries={logbook}
            onSelect={setNovedadAbierta}
            vacio="Nadie relató novedades dentro de la ventana de este viaje."
          />
          {logbook.some(e => !e.tripId) && (
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
              Las que no están asignadas a este viaje entraron por su <strong>ventana</strong>: si
              corriges las horas del viaje, esta lista cambia sola.
            </p>
          )}
        </div>

        {/* Los gastos, como las novedades, se DERIVAN de la ventana: nadie
            los asignó a este viaje. Se leen del más antiguo al más reciente,
            que es como el backend los entrega y como se lee un viaje.
            La suma es de lo derivado, y por eso puede cambiar sola al corregir
            las horas del viaje — es justamente lo que ADR-024 hace visible. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <ReceiptIcon size={15} style={{ color: 'var(--accent)' }} />
              Gastos del viaje ({expenses.length})
            </h4>
            {expenses.length > 0 && (
              <strong style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {formatearCOP(sumarMontos(expenses.map(e => e.amount)))}
              </strong>
            )}
          </div>

          {expenses.length === 0 ? (
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
              Nadie registró gastos dentro de la ventana de este viaje.
            </p>
          ) : (
            <>
              {/* La corrección se hace desde el detalle del gasto (ExpenseDetailModal),
                  no desde esta tabla: así se ve antes qué se cambia. Si la
                  corrección mueve la fecha fuera de la ventana, el gasto sale de
                  este viaje y `avisoFuera` lo explica (ADR-024). */}
              <ExpensesTable
                expenses={expenses}
                role={role}
                onVerDetalle={setGastoAbierto}
              />
              {expenses.some(e => !e.tripId) && (
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                  Los que no están asignados a este viaje entraron por su <strong>ventana</strong>:
                  si corriges las horas del viaje, esta lista y su suma cambian solas.
                </p>
              )}
            </>
          )}
        </div>

        {/* FE-32 — los ingresos, como los gastos, se DERIVAN de la ventana
            (ADR-024). Se MIRAN desde aquí; se corrigen desde Ingresos.

            El Operador no ve este bloque desde el #225 (issue #228): el importe
            que se le cobra al cliente no es suyo. `useTripDetail` tampoco pide
            la lista en ese caso, así que aquí llega vacía — la guarda está por
            lo que DICE, no porque haga falta para que no se pinte nada: sin
            ella, un Operador vería «Ingresos del viaje (0) · Nadie registró
            ingresos…», que es una afirmación falsa sobre un viaje que sí puede
            tener ingresos.

            Esto NO le quita el manifiesto: FE-34 lo puso donde tocaba, en la
            tarjeta del viaje en curso («Agregar manifiesto»), y su permiso
            cuelga de `puedeFinalizar` —`ActionRunTrip`—, no de
            `puedeRegistrarIngreso`. Subir la foto de su viaje y ver lo que se
            le cobra al cliente son dos cosas distintas, y el reparto lo
            refleja. */}
        {puedeVerIngresos(user) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Banknote size={15} style={{ color: '#10B981' }} />
              Ingresos del viaje ({revenues.length})
            </h4>
            {revenues.length > 0 && (
              <strong style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {formatearCOP(sumarMontos(revenues.map(r => r.amount)))}
              </strong>
            )}
          </div>
          {revenues.length === 0 ? (
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
              Nadie registró ingresos dentro de la ventana de este viaje.
            </p>
          ) : (
            <>
              <RevenuesTable revenues={revenues} role={role} onVerDetalle={setIngresoAbierto} />
              {revenues.some(r => !r.tripId) && (
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                  Los que no están asignados a este viaje entraron por su <strong>ventana</strong>:
                  si corriges las horas del viaje, esta lista y su suma cambian solas.
                </p>
              )}
            </>
          )}
        </div>
        )}

        <TripTimeline transitions={transitions} />
      </div>

      <RevenueDetailModal
        revenue={ingresoAbierto}
        onClose={() => setIngresoAbierto(null)}
        puedeCorregir={false}
        puedeSubirSoporte={puedeRegistrarIngreso(user) && puedeCorregirIngreso(user) && (ingresoAbierto ? dentroDeVentanaEdicionIngreso(ingresoAbierto, role) : false)}
        onSoporteSubido={() => reload()}
      />

      <LogbookEntryDetailModal
        entry={novedadAbierta}
        onClose={() => setNovedadAbierta(null)}
        puedeCorregir={puedeCorregirNovedad(role)}
        ventanaVencida={novedadAbierta ? !ventanaNovedad(novedadAbierta, role) : false}
        puedeAdjuntar={puedeAdjuntar(role)}
        onCorregir={e => { setNovedadAbierta(null); setCorrigiendoNovedad(e); }}
      />

      <ExpenseDetailModal
        expense={gastoAbierto}
        onClose={() => setGastoAbierto(null)}
        // Paridad con el panel de Gastos: desde el viaje también se corrige.
        // Si la corrección mueve la fecha fuera de la ventana, `avisoFuera` lo
        // dice y el gasto queda en su panel (ADR-024).
        puedeCorregir={puedeCorregirGasto(role)}
        ventanaVencida={gastoAbierto ? !ventanaGasto(gastoAbierto, role) : false}
        onCorregir={e => { setGastoAbierto(null); setCorrigiendoGasto(e); }}
        puedeSubirRecibo={puedeSubirRecibo(role)}
        // Adjuntar el recibo que faltaba SÍ se puede desde aquí: no mueve el
        // gasto de viaje, solo le añade el soporte. Se recarga el expediente
        // para que la fila deje de decir «Falta».
        onReciboSubido={() => { setGastoAbierto(null); reload(); }}
      />

      <ExpenseFormModal
        isOpen={corrigiendoGasto !== null}
        onClose={() => setCorrigiendoGasto(null)}
        expense={corrigiendoGasto}
        categorias={categories}
        vehicles={vehicles}
        drivers={drivers}
        driverAutomatico={role === 'operator'}
        onRegistrar={() => Promise.reject(new Error('Desde el viaje solo se corrigen gastos existentes'))}
        onCorregir={corregirGasto}
      />

      <LogbookEntryFormModal
        isOpen={corrigiendoNovedad !== null}
        onClose={() => setCorrigiendoNovedad(null)}
        entry={corrigiendoNovedad}
        tipos={tiposNovedad}
        vehicles={vehicles}
        drivers={drivers}
        onRegistrar={() => Promise.reject(new Error('Desde el viaje solo se corrigen novedades existentes'))}
        onCorregir={corregirNovedad}
      />

      <AlertExpandedModal
        isOpen={alertaAbierta !== null}
        alert={alertaAbierta}
        onClose={() => setAlertaAbierta(null)}
        onResolveAlert={(id, reason) => { void closeAlert(id, reason).then(() => { setAlertaAbierta(null); reload(); }).catch(() => { /* el modal queda abierto; el backend rechazó */ }); }}
      />
    </Modal>
  );
}
